import React, { useState, useContext, useEffect } from 'react';
import { get } from 'lodash';
import './QuestionnaireReportChart.scss';
import { BarChart } from '../BarChart/BarChart';
import { PieChart } from '../PieChart/PieChart';
import { Button } from '../../Form/Button/Button';
import { I18nReactContext } from '../../../contexts/I18nContext';
import { questionnaireQuestionTypes } from '../../Questionnaires/QuestionnaireConstants';

/**
 * Main survey chart container component that can display both bar and pie charts
 * Allows switching between chart types and provides consistent styling
 * Also displays free text responses when applicable
 * 
 * @param {object} params - Component parameters
 * @param {Array} params.surveyData - Chart data array with labels and values
 * @param {string} params.questionText - The survey question text to display as title
 * @param {number} params.questionNumber - The question number to display
 * @param {string} [params.chartType] - Initial chart type: 'bar' or 'pie' (default: auto-determined)
 * @param {boolean} params.allowChartTypeSwitch - Whether to show chart type toggle buttons (default: true)
 * @param {boolean} params.showMetadata - Whether to show question metadata (default: true)
 * @param {number} params.height - Chart height in pixels (default: 350)
 * @param {string} [params.variant] - Pie chart variant: 'pie' or 'doughnut' (default: 'pie')
 * @param {Array} params.freeTextResponses - Array of free text responses for text-based questions
 * @param {number} params.totalResponses - Total number of responses for the question
 * @param {string} params.questionType - Type of the question
 * @param {string} [params.labelText] - Text to display as label (default: '')
 * @returns {JSX.Element} Rendered survey chart component
 */
export const QuestionnaireReportChart = ({ 
    surveyData,
    questionText,
    questionNumber,
    chartType,
    allowChartTypeSwitch = true,
    showMetadata = true,
    height = 350,
    variant = 'doughnut',
    totalResponses,
    questionType,
    labelText
}) => {
    const { i18n } = useContext(I18nReactContext);
    const [currentChartType, setCurrentChartType] = useState(null);

    useEffect(() => {
        if (chartType) {
            setCurrentChartType(chartType);
            return;
        }

        // Determine default chart type based on number of responses
        // If more than 5 different answers, use bar chart; otherwise use pie chart
        const meaningfulResponses = get(surveyData, 'length') > 0
            ? surveyData.filter(item => get(item, 'value', 0) > 0)
            : [];
        setCurrentChartType(meaningfulResponses.length > 5 ? 'bar' : 'pie');
    }, []);

    let totalAnswers = 0;
    // Calculate total responses for metadata
    if (questionType === questionnaireQuestionTypes.FREE_TEXT) {
        totalAnswers = surveyData.length;
    } else {
        totalAnswers = surveyData?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;
    }

    
    /**
     * Handles chart type switching
     * @param {object} params - Function parameters
     * @param {string} params.newChartType - The new chart type to switch to
     * @returns {void}
     */
    const handleChartTypeChange = ({ newChartType }) => {
        setCurrentChartType(newChartType);
    };

    // Validate data - show free text if available, otherwise validate chart data
    if (surveyData.length === 0) {
        return (
            <div className="SurveyChart">
                {questionText && (
                    <div className="questionTitleSection">
                        <h3 className="questionTitle">{questionText}</h3>
                    </div>
                )}

                {questionNumber && (
                    <div className="chartHeader">
                        <div className="chartMetadata">
                            <span className="questionNumber">
                                {i18n.translate('surveyResults.questionNumber', { context: { number: questionNumber } })}
                            </span>
                        </div>
                    </div>
                )}

                <div className="noDataMessage">
                    <p>{i18n.translate('surveyCharts.noData.title')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="SurveyChart">
            {questionText && (
                <div className="questionTitleSection">
                    <h3 className="questionTitle">{questionText}</h3>
                </div>
            )}

            <div className="chartHeader">
                {showMetadata && (
                    <div className="chartMetadata">
                        {questionNumber && (
                            <span className="questionNumber">
                                {i18n.translate('surveyResults.questionNumber', { context: { number: questionNumber } })}
                            </span>
                        )}
                        <span className="responseFromUsers">
                            {i18n.translate('surveyCharts.metadata.responseFromUsers', {
                                context: { responses: totalAnswers }
                            })}
                        </span>
                        {questionType !== 'unknown' && (
                            <span className="questionType">
                                {i18n.translate('surveyCharts.metadata.questionType', {
                                    context: { type: i18n.translate(`questionnaireQuestions.questionOptions.${questionType}`) }
                                })}
                            </span>
                        )}
                    </div>
                )}

                {allowChartTypeSwitch && questionType !== questionnaireQuestionTypes.FREE_TEXT && (
                    <div className="chartTypeToggle">
                        <div className="toggleContainer">
                            <div 
                                className={`toggleOption ${currentChartType === 'bar' ? 'active' : ''}`}
                                onClick={() => handleChartTypeChange({ newChartType: 'bar' })}
                            >
                                📊 {String(i18n.translate('surveyCharts.chartType.bar'))}
                            </div>
                            <div 
                                className={`toggleOption ${currentChartType === 'pie' ? 'active' : ''}`}
                                onClick={() => handleChartTypeChange({ newChartType: 'pie' })}
                            >
                                🥧 {String(i18n.translate('surveyCharts.chartType.pie'))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Chart Content */}
            <div className="chartContent">
                {questionType === questionnaireQuestionTypes.FREE_TEXT ? (
                    <div className="freeTextResponsesDisplay" style={{ minHeight: `${height}px` }}>
                        <div className="freeTextResponsesList">
                            {surveyData.map((response, index) => (
                                    <div key={index} className="freeTextResponseItem">
                                        <span className="responseNumber">{index + 1}</span>
                                        <span className="responseText">{response}</span>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                ) : currentChartType === 'bar' ? (
                    <BarChart
                        data={surveyData}
                        questionText={questionText}
                        responsive={true}
                        height={height}
                        showDataLabels={true}
                    />
                ) : (
                    <PieChart
                        data={surveyData}
                        questionText={questionText}
                        variant={variant}
                        height={height}
                        showPercentages={true}
                        excludeZeroValues={false}
                        showLegend={true}
                        labelText={labelText}
                    />
                )}
            </div>
        </div>
    );
}; 